import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import ProjectContext from '../../context/ProjectContext';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { useOrganizations } from '../../hooks/useOrganizations';
import axios from 'axios';

const Navbar = () => {
  const { isAuthenticated, logout, user, loading: authLoading } = useContext(AuthContext);
  const { activeProject, projects, setActiveProject } = useContext(ProjectContext);
  const { organizations } = useOrganizations(user, authLoading);
  const [showModal, setShowModal] = useState(false);
  
  // Organization state for filtering inside the modal
  const [selectedOrgId, setSelectedOrgId] = useState('');

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    password: '',
    confirmPassword: ''
  });
  const [passwordStatus, setPasswordStatus] = useState(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswordText, setShowPasswordText] = useState(false);

  // Synchronize selected organization with the current active project
  useEffect(() => {
    if (activeProject && activeProject.organizationId) {
      setSelectedOrgId(activeProject.organizationId);
    }
  }, [activeProject, showModal]);

  // Reset password states when modal opens/closes
  useEffect(() => {
    if (!showModal) {
      setShowPasswordForm(false);
      setPasswordForm({ password: '', confirmPassword: '' });
      setPasswordStatus(null);
    }
  }, [showModal]);

  const onLogout = (e) => {
    e.preventDefault();
    logout();
  };

  const handleOrgChange = (e) => {
    setSelectedOrgId(e.target.value);
  };

  const handleProjectChange = (e) => {
    const selectedId = e.target.value;
    const project = projects.find(p => p._id === selectedId);
    if (project) {
      setActiveProject(project);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.password !== passwordForm.confirmPassword) {
      return setPasswordStatus({ type: 'danger', message: 'Passwords do not match.' });
    }
    if (passwordForm.password.length < 6) {
      return setPasswordStatus({ type: 'danger', message: 'Password must be at least 6 characters.' });
    }

    setPasswordLoading(true);
    setPasswordStatus(null);
    try {
      await axios.put('/api/users/profile', { password: passwordForm.password });
      setPasswordStatus({ type: 'success', message: 'Password updated successfully!' });
      setPasswordForm({ password: '', confirmPassword: '' });
      setTimeout(() => setShowPasswordForm(false), 2000);
    } catch (err) {
      console.error(err);
      setPasswordStatus({ type: 'danger', message: err.response?.data?.msg || 'Failed to update password.' });
    } finally {
      setPasswordLoading(false);
    }
  };

  // Filter projects by organization
  const filteredProjects = selectedOrgId
    ? projects.filter(p => p.organizationId === selectedOrgId)
    : [];

  if (!isAuthenticated) return null;

  return (
    <div className="sidebar">
      <Link className="sidebar-brand" to="/orgs">{activeProject ? activeProject.name : 'Bookeeping'}</Link>

      <div className="sidebar-nav">
        <div className="nav-group-header">General</div>
        <ul className="list-unstyled">
          <li className="nav-item">
            <Link className="nav-link" to="/orgs"><i className="fas fa-th-large"></i> Organizations</Link>
          </li>
          
          {activeProject && (
            <>
              <li className="nav-item">
                <Link className="nav-link" to={`/org/${activeProject.organizationId}/dashboard`}>
                    <i className="fas fa-chart-pie"></i> Org Dashboard
                </Link>
              </li>
              <div className="nav-divider"></div>
              <li className="nav-item">
                <Link className="nav-link" to="/transactions"><i className="fas fa-exchange-alt"></i> Transactions</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/reconcile"><i className="fas fa-check-double"></i> Reconcile</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/accounts"><i className="fas fa-university"></i> Accounts</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/payees"><i className="fas fa-users"></i> Payees</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/classes"><i className="fas fa-tags"></i> Classes</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/reports"><i className="fas fa-chart-line"></i> Reports</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/rules"><i className="fas fa-magic"></i> Rules</Link>
              </li>
            </>
          )}
          
          {user?.type === 'admin' && (
            <li className="nav-item">
              <Link className="nav-link" to="/documents"><i className="far fa-file-alt"></i> Documents</Link>
            </li>
          )}
        </ul>

        {user && user.type === 'admin' && (
          <>
            <div className="nav-group-header">Administration</div>
            <ul className="list-unstyled">
              <li className="nav-item">
                <Link className="nav-link" to="/admin">
                  <i className="fas fa-user-shield"></i> Admin Portal
                </Link>
              </li>
            </ul>
          </>
        )}
      </div>

      <div className="sidebar-footer" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', padding: '10px 15px' }}>
        <button 
          onClick={() => setShowModal(true)}
          className="btn btn-link text-white text-decoration-none p-1 w-100 d-flex align-items-center justify-content-between text-left"
          style={{ outline: 'none', boxShadow: 'none', cursor: 'pointer' }}
        >
          <div className="d-flex align-items-center text-truncate mr-2">
            {/* Round Initials Avatar */}
            <div className="d-flex align-items-center justify-content-center bg-white text-primary rounded-circle mr-2 font-weight-bold" style={{ width: '32px', height: '32px', minWidth: '32px', fontSize: '0.85rem' }}>
              {user && user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
            </div>
            <div className="text-truncate" style={{ fontSize: '0.85rem', lineHeight: '1.3' }}>
              <div className="font-weight-bold text-white text-truncate">{user ? user.name : 'User'}</div>
              <div className="text-white-50 text-truncate" style={{ fontSize: '0.75rem' }}>
                {activeProject ? activeProject.name : 'No Project Selected'}
              </div>
            </div>
          </div>
          <i className="fas fa-cog text-white-50" style={{ fontSize: '0.95rem' }}></i>
        </button>
      </div>

      {/* Unified User Settings & Permanent Project Selector Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
        <Modal.Header closeButton className="py-2 px-3 bg-light">
          <Modal.Title className="h6 mb-0 text-dark">User Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <div className="text-center mb-3 pb-3 border-bottom">
            <div className="d-inline-flex align-items-center justify-content-center bg-primary text-white rounded-circle font-weight-bold mb-2" style={{ width: '48px', height: '48px', fontSize: '1.2rem' }}>
              {user && user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
            </div>
            <h6 className="font-weight-bold mb-0 text-dark">{user && user.name}</h6>
            <small className="text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>{user && user.type}</small>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="small text-muted mb-1 font-weight-bold">PERMANENT ORG</Form.Label>
            <Form.Control 
              as="select" 
              size="sm" 
              value={selectedOrgId} 
              onChange={handleOrgChange}
            >
              <option value="">Select an organization...</option>
              {organizations.map(org => (
                <option key={org._id} value={org._id}>
                  {org.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small text-muted mb-1 font-weight-bold">PERMANENT PROJECT</Form.Label>
            <Form.Control 
              as="select" 
              size="sm" 
              value={activeProject ? activeProject._id : ''} 
              onChange={handleProjectChange}
              disabled={!selectedOrgId}
            >
              <option value="">Select a project...</option>
              {filteredProjects.map(project => (
                <option key={project._id} value={project._id}>
                  {project.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          {/* Change Password Block */}
          <div className="mb-3">
            <Button 
              variant="link" 
              className="p-0 text-decoration-none small text-muted font-weight-bold" 
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
            >
              <i className={`fas ${showPasswordForm ? 'fa-chevron-down' : 'fa-chevron-right'} mr-1`}></i> CHANGE PASSWORD
            </Button>

            {showPasswordForm && (
              <Form onSubmit={handlePasswordSubmit} className="mt-2 p-2 border rounded bg-light">
                {passwordStatus && (
                  <Alert variant={passwordStatus.type} className="py-1 px-2 mb-2 border-0 extra-small" style={{ fontSize: '0.7rem' }}>
                    {passwordStatus.message}
                  </Alert>
                )}
                <Form.Group className="mb-2">
                  <Form.Label className="extra-small font-weight-bold text-muted mb-1">NEW PASSWORD</Form.Label>
                  <div className="input-group">
                    <Form.Control 
                      type={showPasswordText ? 'text' : 'password'} 
                      size="sm" 
                      required 
                      value={passwordForm.password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                      placeholder="At least 6 chars..."
                      style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    />
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={() => setShowPasswordText(!showPasswordText)}
                      style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 0, padding: '0 8px' }}
                    >
                      <i className={`fas ${showPasswordText ? 'fa-eye-slash' : 'fa-eye'}`} style={{ fontSize: '0.75rem' }}></i>
                    </Button>
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label className="extra-small font-weight-bold text-muted mb-1">CONFIRM PASSWORD</Form.Label>
                  <div className="input-group">
                    <Form.Control 
                      type={showPasswordText ? 'text' : 'password'} 
                      size="sm" 
                      required 
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      placeholder="Confirm new password..."
                      style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    />
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={() => setShowPasswordText(!showPasswordText)}
                      style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 0, padding: '0 8px' }}
                    >
                      <i className={`fas ${showPasswordText ? 'fa-eye-slash' : 'fa-eye'}`} style={{ fontSize: '0.75rem' }}></i>
                    </Button>
                  </div>
                </Form.Group>
                
                <Button type="submit" variant="primary" size="sm" block disabled={passwordLoading} className="py-1" style={{ fontSize: '0.75rem' }}>
                  {passwordLoading ? 'Updating...' : 'Update Password'}
                </Button>
              </Form>
            )}
          </div>

          <hr className="my-3" />

          <div className="d-flex justify-content-between align-items-center">
            <Button 
              variant="link" 
              className="text-danger p-0 small text-decoration-none"
              onClick={(e) => {
                setShowModal(false);
                onLogout(e);
              }}
              style={{ fontSize: '0.8rem' }}
            >
              <i className="fas fa-sign-out-alt mr-1"></i> Log Out
            </Button>
            
            <Button 
              variant="primary" 
              size="sm" 
              onClick={() => setShowModal(false)}
              className="px-3"
            >
              Save & Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Navbar;
